import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { FeatureToggles } from '@makemydeal/dr-platform-shared';

import { ReduxMiddleware, ReduxNext, ReduxStore, bootstrapActionTypes } from '@makemydeal/dr-shared-store';
import { payloadUtils } from '@makemydeal/dr-shared-ui-utils';
import { CommonOrgDealerVehicle } from '@makemydeal/dr-shared-types/vehicleTypes';
import {
    ConfigChangeVehicle,
    MiddlewareManager,
    NEW_VEHICLE_SELECTED,
    FETCH_CVI_REQUEST,
    FETCH_CVI_FAILURE,
    SEARCH_VEHICLE_REQUEST,
    SEARCH_VEHICLE_FAILURE,
    PROCESS_CVI_VEHICLES,
    UPDATE_VEHICLE_CUSTOM_DATA,
    UPDATE_CUSTOM_VEHICLE,
    FETCH_CATALOG_VEHICLES_SUCCESS,
    CLEAR_CATALOG_VEHICLES,
    FETCH_CATALOG_VEHICLES_FAILURE,
    FETCH_CATALOG_MAKES_FAILURE,
    FETCH_CATALOG_MODELS_FAILURE,
    FETCH_CATALOG_TRIMS_FAILURE,
    FETCH_CATALOG_YEARS_FAILURE,
    SET_APPLY_VEHICLE_REQUESTED,
    changeVehicleCreators
} from '@makemydeal/dr-activities-change-vehicle';

import { changeVehicleSelectors, configureChangeVehicle } from './config';
import { getServicesBff } from '../../selectors/chooseVehicleAppSelectors';
import { APP_NAME } from '../../../../constants';
import { ChooseVehicleStateTree } from '../../types/stateTreeTypes';
import {
    sendCatalogVehicleFailureMsg,
    sendInitSuccessMsg,
    sendCatalogVehicleSuccessMsg,
    sendSearchFailureMsg,
    sendSearchSuccessMsg,
    sendSelectionChangedMsg,
    sendCDLEventMsg,
    sendAIVehicleSelectedMsg,
    sendAIBuildVehicleSelectedMsg,
    sendApplyVehicleMsg
} from './postMessageUtils/postMessageUtils';

import { CDL_V3_EVENT, AI_VEHICLE_SELECTED, AI_BUILD_VEHICLE_SELECTED } from '@makemydeal/dr-activities-change-vehicle';

// keep a cached version of the middleware once we're able to build it.
// exported to support testing
export let cachedChangeVehicleMiddlewareMgr: MiddlewareManager | undefined;

// needed for testing
export const changeVehicleMiddlewareClearCache = () => (cachedChangeVehicleMiddlewareMgr = undefined);

export const changeVehicleMiddleware: ReduxMiddleware<ChooseVehicleStateTree> =
    (store: ReduxStore<ChooseVehicleStateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
        const forwardAction = () => {
            if (cachedChangeVehicleMiddlewareMgr) {
                cachedChangeVehicleMiddlewareMgr.middleware(store)(next)(action);
            } else {
                next(action);
            }
        };

        switch (action.type) {
            // Handle CDL v3 events from library and forward to Sales View
            case CDL_V3_EVENT: {
                sendCDLEventMsg(action.payload);
                break;
            }
            case AI_VEHICLE_SELECTED: {
                sendAIVehicleSelectedMsg(action.payload);
                break;
            }
            case AI_BUILD_VEHICLE_SELECTED: {
                sendAIBuildVehicleSelectedMsg(action.payload);
                break;
            }
            case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
                // get what we can from state.  This typically will be the bff endpoint only since its placed as part of initial state
                const state = store.getState();
                const bff = getServicesBff(state);
                const featureToggles: FeatureToggles = payloadUtils.getFeatureTogglesFromInitPayload(action.payload);

                const initConfig: ConfigChangeVehicle = {
                    applicationName: APP_NAME,
                    services: {
                        bff
                    },
                    featureToggles
                };

                cachedChangeVehicleMiddlewareMgr = configureChangeVehicle(initConfig);
                sendInitSuccessMsg();

                break;
            }
            case NEW_VEHICLE_SELECTED: {
                const state = store.getState();
                const selectedVehicleVin = action.payload.vehicle.vin;

                // Case 1: Search Vehicle
                if (selectedVehicleVin) {
                    const selectedVehicleWithCustomData =
                        changeVehicleSelectors.getVehicleWithCustomData(selectedVehicleVin)(state);
                    sendSelectionChangedMsg(selectedVehicleWithCustomData);
                }

                // Case 2: Build Vehicle
                else {
                    const selectedVehicleData = action.payload.vehicle;
                    sendSelectionChangedMsg(selectedVehicleData);
                }

                break;
            }
            case SET_APPLY_VEHICLE_REQUESTED: {
                // A result row raises this to ask for its selected vehicle to be applied. In every other host the
                // footer consumes it; this app renders no footer, so the request has to cross the frame to the host
                // that owns the switch. Only a true payload is a request — the clear below comes back through here.
                if (!action.payload) {
                    break;
                }

                const state = store.getState();
                let switchRequested = false;

                // A switch already in flight must not be started again. The row's button stays clickable while one
                // runs — its loading state is an overlay, not a disabled state — so this is the only thing standing
                // between a second click and a second switch. Same guard the footer applies for the other hosts.
                if (!changeVehicleSelectors.isChangeProcessing(state)) {
                    const selectedVehicle = changeVehicleSelectors.getSelectedVehicle(state);

                    // Inventory (vin + stockNumber) carries the dealer's price/odometer edits keyed by vin. A build
                    // vehicle keeps the entered msrp/selling price/odometer in customVehicle and may still carry the
                    // vin of the row it was built from, so it is merged the same way the footer does for other hosts.
                    // Without that the host gets no prices, rejects the vehicle and the row spinner never stops.
                    const vehicleToApply =
                        selectedVehicle.vin && selectedVehicle.stockNumber
                            ? changeVehicleSelectors.getVehicleWithCustomData(selectedVehicle.vin)(state)
                            : changeVehicleSelectors.getSelectedVehicleWithCustomData(state);

                    // getSelectedVehicle falls back to an empty object rather than null, so identity is what makes a
                    // vehicle real. Nothing is sent otherwise — better no switch than a switch to the wrong vehicle.
                    if (vehicleToApply?.vin || vehicleToApply?.chromeStyleId) {
                        sendApplyVehicleMsg(vehicleToApply);
                        switchRequested = true;
                    }
                }

                // Forwarded before the dispatches below so the reducer sees them last. Otherwise this action's own
                // payload of `true` lands after the clear and leaves the request raised.
                forwardAction();

                if (switchRequested) {
                    // The switch runs in the host, outside this frame, so nothing here would otherwise know it
                    // started and the row's button would sit inert. Cleared again when the host reports back
                    // through CHANGE_SUCCESS / CHANGE_FAILURE.
                    store.dispatch(changeVehicleCreators.processSwitchVehicle());
                }

                // Cleared for the same reason the footer clears it elsewhere: one request must not survive to be
                // acted on twice.
                store.dispatch(changeVehicleCreators.setApplyVehicleRequested(false));

                return;
            }
            case UPDATE_CUSTOM_VEHICLE: {
                const state = store.getState();
                const selectedVehicle = changeVehicleSelectors.getSelectedVehicle(state);

                if (action.payload && Object.keys(action.payload).length) {
                    const { msrp: retailPrice, odometer: mileage, sellingPrice: listedPrice } = action.payload;

                    sendSelectionChangedMsg({
                        ...selectedVehicle,
                        ...(retailPrice !== undefined && { retailPrice }),
                        ...(mileage !== undefined && { mileage }),
                        ...(listedPrice !== undefined && { listedPrice })
                    });
                }

                break;
            }
            case UPDATE_VEHICLE_CUSTOM_DATA: {
                const state = store.getState();
                const selectedVehicle = changeVehicleSelectors.getSelectedVehicle(state);
                const { listedPrice, mileage, retailPrice } = action.payload.vehicleCustomData;

                sendSelectionChangedMsg({
                    ...selectedVehicle,
                    ...(listedPrice !== undefined && { listedPrice }),
                    ...(mileage !== undefined && { mileage }),
                    ...(retailPrice !== undefined && { retailPrice })
                });

                break;
            }
            case SEARCH_VEHICLE_REQUEST:
            case FETCH_CVI_REQUEST:
            case CLEAR_CATALOG_VEHICLES: {
                sendSelectionChangedMsg(null);

                break;
            }
            case PROCESS_CVI_VEHICLES: {
                const vehicles = action.payload;
                sendSearchSuccessMsg(vehicles);

                break;
            }
            case FETCH_CVI_FAILURE:
            case SEARCH_VEHICLE_FAILURE: {
                if (action.meta?.isAborted) {
                    break;
                }

                sendSearchFailureMsg(action.payload);

                break;
            }
            case FETCH_CATALOG_VEHICLES_SUCCESS: {
                const catalogVehicles = action.payload.filter(({ chromeId }: CommonOrgDealerVehicle) => chromeId);
                sendCatalogVehicleSuccessMsg({ hasVehicles: !!catalogVehicles.length });

                break;
            }
            case FETCH_CATALOG_YEARS_FAILURE:
            case FETCH_CATALOG_MAKES_FAILURE:
            case FETCH_CATALOG_MODELS_FAILURE:
            case FETCH_CATALOG_TRIMS_FAILURE:
            case FETCH_CATALOG_VEHICLES_FAILURE: {
                const error = {
                    ...action.payload
                };
                sendCatalogVehicleFailureMsg(error);

                break;
            }
        }

        forwardAction();
    };
